import { Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { DividerComponent } from "../../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import {
  Button,
  IconButton,
  LoadingIndicator,
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { SquarePen, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useSearchParams } from "react-router-dom";
import {
  RouteSearchParam,
  useRouteNavigator,
} from "../../../../../routes/RoutesHelper";
import { GetEmailTemplateDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailTemplateDetailsRPC";
import { getValidatedLeoUUIDFromSearchParam } from "../../../../utils/getValidatedLeoUUIDFromSearchParam";
import {
  useDeleteEmailTemplateStore,
  useEmailTemplateDetailsStore,
} from "../../../stores/Hooks";
import { EmailTemplateContentRTE } from "../../inputs/EmailTemplateContentRTE";
import { Mode } from "../../../../enums/Mode";
import { useSignInStore } from "../../../../store/Hooks";
import { UserPrivileges } from "../../../../store/user/UserPrivileges";
import { useBorder } from "@pulse/shared-components";

interface EmailTemplateDetailsDrawerProps {
  handleDrawerClose: VoidFunction;
  setOpenUnexpectedErrorDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenConfirmDeleteEmailTemplateDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const EmailTemplateDetailsDrawerContent: React.FC<EmailTemplateDetailsDrawerProps> =
  observer(
    ({
      handleDrawerClose,
      setOpenUnexpectedErrorDialog,
      setOpenConfirmDeleteEmailTemplateDialog,
    }) => {
      const [searchParams, setSearchParams] = useSearchParams();

      const { t } = useTranslation();
      const tokens = useFoundationColorTokens();
      const typography = useTypography();
      const spacing = useSpacing();
      const navigateTo = useRouteNavigator();
      const emailTemplateDetailsStore = useEmailTemplateDetailsStore();
      const signInStore = useSignInStore();
      const deleteEmailTemplateStore = useDeleteEmailTemplateStore();
      const border = useBorder();
      const cornerRadius = useCornerRadius();

      useEffect(() => {
        const templateId = getValidatedLeoUUIDFromSearchParam(
          searchParams,
          RouteSearchParam.TemplateId,
        );
        if (!templateId) return navigateTo.pageNotFound();

        emailTemplateDetailsStore
          .getEmailTemplateDetails(templateId)
          .catch(() => {
            handleDrawerClose();
            setOpenUnexpectedErrorDialog(true);
          });
      }, []);

      useEffect(() => {
        if (!emailTemplateDetailsStore.getEmailTemplateDetailsRPCError) return;

        switch (emailTemplateDetailsStore.getEmailTemplateDetailsRPCError) {
          case GetEmailTemplateDetailsRPC.RPCError.InvalidTemplateId:
            navigateTo.pageNotFound();
            break;
          default:
            emailTemplateDetailsStore.getEmailTemplateDetailsRPCError satisfies never;
        }
      }, [emailTemplateDetailsStore.getEmailTemplateDetailsRPCError]);

      return (
        <Stack
          divider={<DividerComponent orientation="horizontal" width="100%" />}
          height="100%"
        >
          {/* Drawer Header */}
          <Stack
            direction="row"
            paddingBlock={spacing.spaceMD}
            paddingInline={spacing.space2XL}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography color={tokens.labelHighEmphasis} {...typography.sh2}>
              {t("emailTemplates.emailTemplateDetails")}
            </Typography>
            <IconButton
              name="createEmailTemplateDrawerCloseButton"
              size="medium"
              icon={<X />}
              variant="plain-neutral"
              onClick={handleDrawerClose}
            />
          </Stack>

          {/* Drawer Body */}
          {emailTemplateDetailsStore.loadingEmailTemplateDetails ? (
            <LoadingIndicator
              variant="container"
              loadingText={t("common.detailsLoadingState")}
              isLoading
            />
          ) : (
            <Stack
              padding={spacing.space2XL}
              flex={1}
              gap={spacing.spaceXL}
              overflow="auto"
            >
              <Stack gap={spacing.spaceXXS}>
                <Typography color={tokens.labelLowEmphasis} {...typography.sh4}>
                  {t("emailTemplates.titleLabel")}:
                </Typography>
                <Stack
                  border={border.default}
                  borderRadius={cornerRadius.radiusXS}
                  paddingInline={spacing.spaceMD}
                  paddingBlock={spacing.spaceSM}
                >
                  <Typography
                    color={tokens.labelHighEmphasis}
                    {...typography.b1}
                  >
                    {emailTemplateDetailsStore.emailTemplateName.name}
                  </Typography>
                </Stack>
              </Stack>

              <Stack gap={spacing.spaceXXS}>
                <Typography color={tokens.labelLowEmphasis} {...typography.sh4}>
                  {t("emailTemplates.contentLabel")}:
                </Typography>
                <EmailTemplateContentRTE
                  mode="preview"
                  initialValue={
                    emailTemplateDetailsStore.emailTemplateContent.content
                  }
                  rteFileService={
                    emailTemplateDetailsStore.emailTemplateContent
                      .rteFileService
                  }
                  hidePreviewEditToggler
                />
              </Stack>
            </Stack>
          )}

          {/* Drawer Footer */}
          {signInStore.isPrivilegeGranted(
            UserPrivileges.processGlobalEmailTemplate,
          ) && (
            <Stack
              direction="row"
              justifyContent="end"
              paddingBlock={spacing.spaceMD}
              paddingInline={spacing.space2XL}
              gap={spacing.spaceMD}
            >
              <Button
                label={t("emailTemplates.deleteEmailTemplate")}
                name="editEmailTemplateButton"
                size="medium"
                variant="outlined-color"
                icon={<Trash2 />}
                onClick={() => {
                  const templateId = getValidatedLeoUUIDFromSearchParam(
                    searchParams,
                    RouteSearchParam.TemplateId,
                  );

                  if (!templateId) return navigateTo.pageNotFound();
                  setOpenConfirmDeleteEmailTemplateDialog(true);
                  deleteEmailTemplateStore.setTemplateId(templateId.uuid);
                  deleteEmailTemplateStore.emailTemplateName.setName(
                    emailTemplateDetailsStore.emailTemplateName.name,
                  );
                }}
                color="destructive"
                disabled={emailTemplateDetailsStore.loadingEmailTemplateDetails}
              />
              <Button
                label={t("emailTemplates.editEmailTemplate")}
                name="editEmailTemplateButton"
                size="medium"
                variant="outlined-color"
                icon={<SquarePen />}
                onClick={() => {
                  setSearchParams((prev) => {
                    prev.set(RouteSearchParam.Mode, Mode.Edit);
                    return prev;
                  });
                }}
                disabled={emailTemplateDetailsStore.loadingEmailTemplateDetails}
              />
            </Stack>
          )}
        </Stack>
      );
    },
  );
